import { Box, Grid, Paper, Typography } from '@mui/material';
import {
  PieChart,
  Pie,
  Cell,
  Legend,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';
import { Table, TableBody, TableHead, TableRow } from '@mui/material';
import { HeadTableCell, RowTableCell, GeneralTableBox } from '../../../common/components';
import { getTotalFromRows } from '../utils/gerencialesUtils';

/**
 * Seccion reusable con tabla y grafico de torta.
 * @param {Object} props - Props del componente.
 * @param {string} props.title - Titulo principal.
 * @param {Array<Object>} props.columns - Columnas para la tabla.
 * @param {Array<Object>} props.rows - Filas de la tabla.
 * @param {Array<Object>} props.pieData - Data para el grafico.
 * @param {string} props.totalLabel - Label del total.
 * @param {number} props.totalCount - Valor total.
 * @returns {JSX.Element} Seccion con tabla y grafico.
 */
const GerencialesPieTableSection = ({
  title,
  columns,
  rows,
  pieData,
  totalLabel,
  totalCount,
  chartId,
}) => {
  const hasData = rows.length > 0;
  const chartTotal = getTotalFromRows(pieData, 'value');
  const legendLabelMax = 25;
  const formatLegendLabel = (value) => {
    const fullLabel = String(value ?? '');
    if (fullLabel.length <= legendLabelMax) {
      return <span title={fullLabel}>{fullLabel}</span>;
    }

    const truncated = `${fullLabel.slice(0, legendLabelMax - 3)}...`;
    return <span title={fullLabel}>{truncated}</span>;
  };

  return (
    <Paper elevation={1} sx={{ mb: 1, mt: 1 }}>
      <Box
        sx={{
          backgroundColor: '#3b3f46',
          color: 'white',
          px: 2.5,
          py: 1.5,
          borderTopLeftRadius: 4,
          borderTopRightRadius: 4,
          fontSize: 30,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          {title}
        </Typography>
      </Box>

      <Box sx={{ p: 2 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={5}>
            <GeneralTableBox>
              <Table size="large">
                <TableHead>
                  <TableRow>
                    {columns.map((column) => (
                      <HeadTableCell
                        key={column.key}
                        sx={{ textAlign: column.align || 'left', color: 'white' }}
                      >
                        {column.label}
                      </HeadTableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {hasData ? (
                    rows.map((row, rowIndex) => (
                      <TableRow key={`${rowIndex}-${row?.label || row?.name || rowIndex}`}>
                        {columns.map((column) => (
                          <RowTableCell
                            key={`${rowIndex}-${column.key}`}
                            sx={{ textAlign: column.align || 'left' }}
                          >
                            {row?.[column.key] ?? '-'}
                          </RowTableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <RowTableCell colSpan={columns.length} sx={{ textAlign: 'center' }}>
                        Sin resultados
                      </RowTableCell>
                    </TableRow>
                  )}
                  <TableRow>
                    {columns.map((column, index) => {
                      let value = '';
                      if (index === 0) value = totalLabel;
                      if (index === columns.length - 1) value = totalCount;
                      return (
                        <RowTableCell
                          key={`total-${column.key}`}
                          sx={{ fontWeight: 'bold', textAlign: column.align || 'left' }}
                        >
                          {value}
                        </RowTableCell>
                      );
                    })}
                  </TableRow>
                </TableBody>
              </Table>
            </GeneralTableBox>
          </Grid>

          <Grid item xs={12} md={7}>
            <Box id={chartId} sx={{ height: { xs: 280, md: 360 } }}>
              {hasData ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      dataKey="value"
                      nameKey="name"
                      cx="45%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={110}
                      paddingAngle={2}
                      label={({ percent }) => (percent > 0 ? `${(percent * 100).toFixed(1)}%` : '')}
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Legend
                      layout="vertical"
                      verticalAlign="middle"
                      align="right"
                      iconSize={30}
                      wrapperStyle={{ fontSize: 16, paddingLeft: 10}}
                      formatter={formatLegendLabel}
                    />
                    <RechartsTooltip
                      formatter={(value) => [
                        value,
                        chartTotal ? `${((value / chartTotal) * 100).toFixed(1)}%` : '0%',
                      ]}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Box
                  sx={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Sin datos para graficar
                  </Typography>
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
};

export default GerencialesPieTableSection;
